/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useFormikContext } from 'formik';
import {
  useBestFitMutation,
} from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Hooks/Api/useBestFitMutation';
import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

/**
 * Custom useSummaryForm hook
 * @param voucherIdentifier
 */
export function useSummaryForm(voucherIdentifier) {
  const isComponentMounted = useRef(false);
  const {
    values, setFieldValue,
  } = useFormikContext();
  const [isLoading, setIsLoading] = useState(true);
  const [voucherIsLoading, setVoucherIsLoading] = useState(false);
  const [previewContract, setPreviewContract] = useState(null);
  const [voucherErrorMessage, setVoucherErrorMessage] = useState(null);
  const [voucherSuccessMessage, setVoucherSuccessMessage] = useState(null);
  const mutation = useBestFitMutation('previewContract');

  const getContractData = useCallback((ignoreVoucher = false) => JSON.stringify({
    studioId: values.studioId,
    voucherCode: values.voucher && !ignoreVoucher ? values.voucher : null,
    contract: {
      rateBundleTermId: parseInt(values.contract, 10), startDate: values.contractStartDateOfUse,
    },
    customer: {
      firstname: values.firstName,
      lastname: values.lastName,
      dateOfBirth: dayjs(values.dateOfBirth)
        .format('YYYY-MM-DD'),
      email: values.email,
      street: values.street,
      houseNumber: values.houseNumber,
      city: values.city,
      zipCode: values.postCode,
      countryCode: values.countryCode,
      paymentChoice: values.paymentChoice,
      bankAccount: {
        iban: values.iban, accountHolder: `${values.firstName} ${values.lastName}`,
      },
      referralCode: values.referralCode ? values.referralCode : null,
    },
  }), [
    values.city,
    values.contract,
    values.contractStartDateOfUse,
    values.countryCode, values.dateOfBirth,
    values.email,
    values.firstName,
    values.houseNumber,
    values.iban,
    values.lastName,
    values.paymentChoice,
    values.postCode,
    values.referralCode,
    values.street,
    values.studioId,
    values.voucher,
  ]);

  /**
     * Handle success
     * @param response
     * @param variables request payload
     * @param isResetCall
     */
  const onSuccess = useCallback((response, variables, isResetCall = false) => {
    // set valid voucherCode to contract
    if (values.voucher && !isResetCall) {
      setFieldValue('voucherCode', values.voucher);
      // special condition for line 67-72 on wrong voucher with valid voucher before
      setVoucherSuccessMessage(response.data.voucherSuccessMessage);
    }
    setPreviewContract(response.data);
    setVoucherIsLoading(false);
    setIsLoading(false);
  }, [setFieldValue, values.voucher]);

  // proxy for success callbacks for reset calls
  const onResetSuccess = useCallback((response, variables) => {
    onSuccess(response, variables, true);
  }, [onSuccess]);

  /**
     * Handle error
     * @param error
     */
  const onError = useCallback((error) => {
    // show error message from voucher when voucher input is given, and reset to
    if (values.voucher) {
      setVoucherErrorMessage(error.response.data.message);
      // we have to request the preview again without the voucher because
      // if we don't the prices would be reduced still because it depends on previewContract
      if (values.voucherCode) {
        // unset voucherCode from contract which were used before
        setFieldValue('voucherCode', null);
        mutation.mutate(getContractData(true), {
          onSuccess: onResetSuccess, onError,
        });
      }
    }
    setVoucherIsLoading(false);
    setIsLoading(false);
  }, [getContractData, mutation, onResetSuccess, setFieldValue, values.voucher, values.voucherCode]);

  /**
     * Preview contract data
     */
  useEffect(() => {
    if (!isComponentMounted.current) {
      isComponentMounted.current = true;
      setIsLoading(true);
      mutation.mutate(getContractData(), {
        onSuccess, onError,
      });
    }
  }, [getContractData, mutation, onError, onSuccess]);

  const validateVoucher = () => {
    if (values[voucherIdentifier]) {
      setVoucherIsLoading(true);
      setVoucherErrorMessage(null);
      setVoucherSuccessMessage(null);
      mutation.mutate(getContractData(), {
        onSuccess, onError,
      });
    }
  };

  return {
    isLoading, previewContract, validateVoucher, voucherErrorMessage, voucherSuccessMessage, voucherIsLoading,
  };
}

useSummaryForm.propTypes = {
  voucherIdentifier: PropTypes.string.isRequired,
};
